import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Flame, Truck, Shield, Clock } from 'lucide-react';
import Button from '../components/Button';
import ProductCard from '../components/ProductCard';
import Card from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import HeroSlider from '../components/HeroSlider';
import { ProductGridSkeleton } from '../components/Skeleton';
import { useProducts } from '../hooks/useProducts';
import { useSettings } from '../hooks/useSettings';
import { useSliders } from '../hooks/useSliders';
import { useCart } from '../context/CartContext';

const Home = () => {
  const { featuredProducts, fetchFeaturedProducts, loading: productsLoading } = useProducts();
  const { settings, loading: settingsLoading } = useSettings();
  const { sliders, loading: slidersLoading } = useSliders();
  const { addItem } = useCart();
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    fetchFeaturedProducts();
  }, [fetchFeaturedProducts]);

  // Animación de entrada escalonada
  useEffect(() => {
    const sections = ['hero', 'features', 'products'];
    sections.forEach((section, index) => {
      setTimeout(() => {
        setIsVisible(prev => ({ ...prev, [section]: true }));
      }, index * 150);
    });
  }, []);

  const handleAddToCart = (product) => {
    addItem(product, 1);
  };

  const whatsappNumber = settings?.whatsapp_number || '+56900000000';
  const whatsappLink = `https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}`;
  const welcomeMessage = settings?.welcome_message || 'Tu gas a domicilio, rápido y seguro';

  const features = [
    { icon: Truck, title: 'Despacho Rápido', desc: 'Entrega el mismo día' },
    { icon: Shield, title: 'Garantía', desc: 'Productos certificados' },
    { icon: Clock, title: 'Horario Extendido', desc: settings?.business_hours || 'Atención hasta tarde' },
    { icon: Phone, title: 'Pedido por WhatsApp', desc: 'Rápido y sencillo' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Slider Section */}
      {!slidersLoading && sliders.length > 0 && (
        <section className="relative pt-20 pb-6 px-4">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/5 -z-10" />
          <div className="max-w-4xl mx-auto">
            <HeroSlider slides={sliders} />
          </div>
        </section>
      )}

      {/* Hero Section */}
      <section 
        className={`relative ${sliders.length > 0 ? 'pt-6' : 'pt-24'} pb-12 px-4 transition-all duration-700 ${
          isVisible.hero ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-secondary/5 -z-10" />
        
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-6">
            <Flame size={16} />
            <span>Gas a domicilio confiable</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-text mb-6 leading-tight">
            Tu gas a domicilio,{' '}
            <span className="text-primary">rápido y seguro</span>
          </h1>
          
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            {welcomeMessage}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/productos">
              <Button size="lg" className="gap-2">
                Ver Productos
                <ArrowRight size={20} />
              </Button>
            </Link>
            <a 
              href={whatsappLink}
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="outline" size="lg" className="gap-2">
                <Phone size={20} />
                Pedir por WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section 
        className={`py-12 px-4 transition-all duration-700 delay-100 ${
          isVisible.products ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-display font-bold text-text">
              Productos Destacados
            </h2>
            <Link 
              to="/productos" 
              className="text-primary font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all"
            >
              Ver todos
              <ArrowRight size={16} />
            </Link>
          </div>

          {productsLoading ? (
            <ProductGridSkeleton count={4} />
          ) : featuredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {featuredProducts.slice(0, 4).map((product, index) => (
                <div 
                  key={product.id}
                  className="animate-fadeInUp"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <ProductCard 
                    product={product}
                    onAddToCart={handleAddToCart}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No hay productos destacados disponibles</p>
            </div>
          )}
        </div>
      </section>

      {/* Features */}
      <section 
        className={`py-8 px-4 transition-all duration-700 delay-200 ${
          isVisible.features ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          {features.map((feature, index) => (
            <Card 
              key={index} 
              padding="small"
              className="text-center hover:shadow-lg transition-shadow"
            >
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary/10 flex items-center justify-center">
                <feature.icon size={24} className="text-primary" />
              </div>
              <h3 className="font-display font-bold text-text text-sm mb-1">
                {feature.title}
              </h3>
              <p className="text-xs text-gray-500">{feature.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Spacer for bottom nav */}
      <div className="h-24" />

      {/* Floating WhatsApp Button */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-24 right-4 z-40 w-14 h-14 bg-success text-white rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform touch-manipulation"
        aria-label="WhatsApp"
      >
        <Phone size={28} />
      </a>
    </div>
  );
};

export default Home;
